import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ArrowRight, FileText, Inbox } from 'lucide-react';
import { partnersApi } from '../../services/partnersApi';
import { SectionHeader } from '../ui/SectionHeader';
import { SkeletonLoader } from '../SkeletonLoader';
import { ReportSourceBadge } from './PartnerPhaseBadge';
import type { PartnerUpdatesSummary, ReportView } from '../../types/partners';
import { partnerPagePath } from '../../utils/partnerLinks';

/** How many of the oldest new reports the dashboard lists. */
const SHOWN = 5;

/**
 * Dashboard: partner updates waiting on staff, and the oldest new ones, so a
 * report does not sit unanswered while the partner waits. Render it only for
 * people with PR access.
 */
export const PartnerUpdatesWidget = () => {
  const [summary, setSummary] = useState<PartnerUpdatesSummary | null>(null);
  const [oldest, setOldest] = useState<ReportView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([partnersApi.updatesSummary(), partnersApi.updates({ status: 'new', limit: 100 })])
      .then(([s, page]) => {
        if (cancelled) return;
        setSummary(s);
        // The list is newest first; the dashboard wants the ones waiting longest.
        setOldest([...page.reports].sort((a, b) => a.created_at - b.created_at).slice(0, SHOWN));
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Partner updates could not be loaded.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section>
      <SectionHeader
        title="Partner updates awaiting action"
        icon={Inbox}
        as="h2"
        count={summary?.open ?? null}
        actions={
          <Link to="/partnerships?tab=updates" className="glass-button-sm">
            <ArrowRight className="w-3.5 h-3.5" />
            Open the inbox
          </Link>
        }
      />
      <div className="glass-card p-4">
        {loading ? (
          <div className="space-y-2" aria-busy="true">
            {Array.from({ length: 3 }).map((_, i) => (
              <SkeletonLoader key={i} className="h-9" />
            ))}
          </div>
        ) : error ? (
          <p className="text-sm text-glass-400">{error}</p>
        ) : !summary || summary.open === 0 ? (
          <p className="text-sm text-glass-400">No partner update is waiting on staff.</p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-glass-300 tabular-nums">
              {summary.new} new, {summary.acknowledged} acknowledged and {summary.scheduled} scheduled.
              {summary.oldest_new_at && <span className="text-glass-400"> The oldest new report came in {formatDistanceToNow(new Date(summary.oldest_new_at), { addSuffix: true })}.</span>}
            </p>
            {oldest.length > 0 && (
              <ul className="divide-y divide-glass-200/10">
                {oldest.map((report) => (
                  <li key={report.id}>
                    <Link
                      to={partnerPagePath(report.creator_id, 'updates', report.id)}
                      className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 hover:bg-glass-200/5 -mx-2 px-2 rounded-md transition-colors"
                    >
                      <FileText className="w-4 h-4 text-glass-400 shrink-0" />
                      <span className="text-sm text-white">{report.pack_name}</span>
                      <span className="font-mono text-sm text-accent-300">{report.version}</span>
                      <span className="text-xs text-glass-400">{report.creator_name}</span>
                      {report.source === 'curseforge' && <ReportSourceBadge source={report.source} />}
                      <span className="text-xs text-glass-500 tabular-nums ml-auto">{formatDistanceToNow(new Date(report.created_at), { addSuffix: true })}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
